import preact, { JSX } from 'preact';

import { forwardRef } from 'preact/compat';

type ElType = {
	div: HTMLDivElement;
	span: HTMLSpanElement;
	h3: HTMLHeadingElement;
	p: HTMLParagraphElement;
	img: HTMLImageElement;
};

interface InteractiveElementProps<T extends keyof ElType> {
	type: T;
	children?: preact.ComponentChildren;
	alt?: T extends 'img' ? string : never;
}

// Helper type to get props for a tag from JSX.IntrinsicElements
type PropsFor<T extends keyof JSX.IntrinsicElements> = JSX.IntrinsicElements[T];

const InteractiveElement = forwardRef(
	<T extends keyof ElType>(
		{ type, children, alt, ...rest }: InteractiveElementProps<T> & PropsFor<T>,
		ref: preact.Ref<HTMLElement>,
	) => {
		switch (type) {
			case 'span':
				return (
					<span
						ref={ref as preact.Ref<HTMLSpanElement>}
						{...(rest as PropsFor<'span'>)}
					>
						{children}
					</span>
				);
			case 'img':
				return (
					<img
						ref={ref as preact.Ref<HTMLImageElement>}
						alt={alt ?? ''}
						{...(rest as PropsFor<'img'>)}
					/>
				);
			case 'p':
				return (
					<p
						ref={ref as preact.Ref<HTMLParagraphElement>}
						{...(rest as PropsFor<'p'>)}
					>
						{children}
					</p>
				);
			case 'h3':
				return (
					<h3
						ref={ref as preact.Ref<HTMLHeadingElement>}
						{...(rest as PropsFor<'h3'>)}
					>
						{children}
					</h3>
				);
			case 'div':
			default:
				return (
					<div
						ref={ref as preact.Ref<HTMLDivElement>}
						{...(rest as PropsFor<'div'>)}
					>
						{children}
					</div>
				);
		}
	},
);

export default InteractiveElement;
